import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
  SortFieldDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type ReceiptItemDTO = {
  id: string;
  itemType: string;
  studentPackageId: string | null;
  privateLessonPackageId: string | null;
  roomBookingId: string | null;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  studentName?: string | null;
  memberId?: string | null;
  studentId?: string | null;
  packageTemplateId?: string | null;
  /** ຈຳນວນຄັ້ງທັງໝົດຂອງແມ່ແບບ — ກວດແບ່ງຄັ້ງຕໍ່ຄລາດ */
  packageTotalSessions?: number | null;
  startDate?: string | null;
  expireDate?: string | null;
  totalHours?: number | null;
  pricePerHour?: number | null;
  roomName?: string | null;
  date?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  contactName?: string | null;
  assistInstructorNames?: string[];
  /** ຄລາດສ໌ທີ່ເລືອກ (package_purchase ທີ່ມີ maxSelectableClasses) */
  selectedClasses?: {
    classTypeName: string;
    levelName: string;
    allowedRuleId?: string | null;
    allocatedSessions?: number;
    remainingSessions?: number;
  }[];
};

export type ReceiptDetailDTO = {
  id: string;
  receiptNo: string;
  issuedAt: string;
  issuedBy: string;
  issuedByName: string | null;
  paymentId: string;
  status: "active" | "voided";
  voidedAt: string | null;
  voidedBy: string | null;
  voidedByName: string | null;
  voidReason: string | null;
  items: ReceiptItemDTO[];
  payment: {
    id: string;
    amount: number;
    paidAt: string;
    paidBy: string;
    discountType?: string | null;
    discountValue?: number | null;
    discountAmount?: number | null;
    originalAmount?: number | null;
  };
};

export type ReceiptRoomBookingAmendAssistRowDTO = {
  instructorId: string;
  instructorName: string;
  instructorCheckInId: string;
  sessionCount: number;
  pricePerSession: number;
  existingReceiptItemId: string | null;
};

export type ReceiptRoomBookingSessionRowDTO = {
  bookingId: string;
  date: string;
  startTime: string;
  endTime: string;
  roomName: string;
  status: "scheduled" | "completed" | "cancelled";
};

export type ReceiptRoomBookingAmendContextDTO = {
  receiptId: string;
  receiptNo: string;
  paymentId: string;
  roomBookingCount: number;
  roomSessionQuantity: number;
  roomBookingId: string | null;
  roomItemsTotal: number;
  assistItemsTotal: number;
  currentPaymentAmount: number;
  currentItemsTotal: number;
  assistRows: ReceiptRoomBookingAmendAssistRowDTO[];
  sessionRows: ReceiptRoomBookingSessionRowDTO[];
  roomUnitPrice: number;
  canAmend: boolean;
  blockReason: string | null;
  baseBlockReason: string | null;
  canAmendSessions: boolean;
  sessionAmendBlockReason: string | null;
  canAmendAssistItems: boolean;
  assistAmendBlockReason: string | null;
  missingSessionDayNumbers: number[];
  needsReceiptCalendarSync: boolean;
};

export type AmendReceiptRoomBookingItemsResultDTO = {
  receiptId: string;
  paymentId: string;
  previousPaymentAmount: number;
  newPaymentAmount: number;
  cancelledBookingCount: number;
  remainingBookingCount: number;
};

export type AmendReceiptRoomBookingAssistResultDTO = {
  receiptId: string;
  paymentId: string;
  previousPaymentAmount: number;
  newPaymentAmount: number;
  assistItemsAdded: number;
};

export type ReceiptListItemDTO = {
  id: string;
  receiptNo: string;
  issuedAt: string;
  issuedBy: string;
  issuedByName: string | null;
  paymentId: string;
  totalAmount: number;
  discountType: string | null;
  discountValue: number | null;
  discountAmount: number | null;
  originalAmount: number | null;
};

export type ReceiptsPageDTO = {
  data: ReceiptListItemDTO[];
  meta: { total: number; offset: number; limit: number };
};

export type ReceiptsSummaryDTO = {
  totalCount: number;
  totalAmount: number;
  discountCount: number;
  totalDiscountAmount: number;
};

export type ReceiptsSummaryQuery = {
  filters?: FilterConditionDTO[];
};

export type VoidReceiptPreviewDTO = {
  receiptId: string;
  receiptNo: string;
  status: string;
  canVoid: boolean;
  blockReason: string | null;
  classPackages: number;
  privateLessonPackages: number;
  roomBookings: number;
  estimatedBookings: number;
  estimatedAttendances: number;
  unsupportedItemTypes: string[];
};

export type VoidReceiptResultDTO = {
  receiptId: string;
  receiptNo: string;
  classPackagesRemoved: number;
  privateLessonPackagesRemoved: number;
  roomBookingsCancelled: number;
  bookingsRemoved: number;
  attendancesRemoved: number;
};

export type CreditInvoiceListItemDTO = {
  id: string;
  refType: string;
  refId: string;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  status: string;
  issuedAt: string;
  notes: string | null;
  bookingDate: string | null;
  bookingStartTime: string | null;
  bookingEndTime: string | null;
  contactName: string | null;
  contactPhone: string | null;
  roomName: string | null;
  studentName: string | null;
  studentMemberId: string | null;
  packageContextLabel: string | null;
  assistInstructorNames?: string[];
};

export type CreditInstallmentRowDTO = {
  id: string;
  installmentNo: number;
  label: string | null;
  dueDate: string | null;
  amount: number;
  paidAmount: number;
  status: "pending" | "partial" | "paid" | "cancelled";
};

export type CreditPaymentRowDTO = {
  id: string;
  amount: number;
  paidAt: string;
  paidBy: string | null;
  paidByName: string | null;
  notes: string | null;
  status: "active" | "voided";
  voidedAt: string | null;
  voidReason: string | null;
};

export type CreditInvoiceDetailDTO = CreditInvoiceListItemDTO & {
  voidedAt?: string | null;
  voidReason?: string | null;
  installments: CreditInstallmentRowDTO[];
  payments: CreditPaymentRowDTO[];
};

export type VoidCreditInvoicePreviewDTO = {
  creditInvoiceId: string;
  refType: string;
  refId: string;
  status: string;
  canVoid: boolean;
  blockReason: string | null;
  classPackages: number;
  privateLessonPackages: number;
  roomBookings: number;
  estimatedBookings: number;
  estimatedAttendances: number;
  activePayments: number;
  linkedReceiptPayments: number;
};

export type VoidCreditInvoiceResultDTO = {
  creditInvoiceId: string;
  refType: string;
  classPackagesRemoved: number;
  privateLessonPackagesRemoved: number;
  roomBookingsCancelled: number;
  bookingsRemoved: number;
  attendancesRemoved: number;
  paymentsVoided: number;
};

export type BulkVoidCreditInvoicesResultDTO = {
  succeeded: VoidCreditInvoiceResultDTO[];
  failed: Array<{ invoiceId: string; error: string }>;
};

export type CreditInvoicesListQuery = {
  limit?: number;
  offset?: number;
  refType?:
    | "room_booking"
    | "package_purchase"
    | "private_lesson_package"
    | "partner_project_sale";
  outstandingOnly?: boolean;
  name?: string;
  sort?: SortFieldDTO[];
};

export const billingApi = {
  receipts: {
    async getSummary(query: ReceiptsSummaryQuery): Promise<ReceiptsSummaryDTO> {
      const url = new URL(`${config.apiUrl}/billing/receipts/summary`);
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<ReceiptsSummaryDTO>(url.toString());
    },
    async list(query: OffsetPageQueryDTO): Promise<ReceiptsPageDTO> {
      const url = new URL(`${config.apiUrl}/billing/receipts`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<ReceiptsPageDTO>(url.toString());
    },
    async getById(id: string): Promise<ReceiptDetailDTO> {
      return fetcher.get<ReceiptDetailDTO>(
        `${config.apiUrl}/billing/receipts/${id}`,
      );
    },
    async getRoomBookingAmendContext(
      id: string,
    ): Promise<ReceiptRoomBookingAmendContextDTO> {
      return fetcher.get<ReceiptRoomBookingAmendContextDTO>(
        `${config.apiUrl}/billing/receipts/${id}/room-booking-amend-context`,
      );
    },
    async amendRoomBookingAssistItems(
      id: string,
      body: { items: Array<{ instructorId: string; pricePerSession: number }> },
    ): Promise<AmendReceiptRoomBookingAssistResultDTO> {
      return fetcher.patch<AmendReceiptRoomBookingAssistResultDTO>(
        `${config.apiUrl}/billing/receipts/${id}/room-booking-assist-items`,
        body,
      );
    },
    async amendRoomBookingItems(
      id: string,
      body: {
        cancelBookingIds?: string[];
        assistItems?: Array<{ instructorId: string; pricePerSession: number }>;
        syncFromCalendar?: boolean;
      },
    ): Promise<AmendReceiptRoomBookingItemsResultDTO> {
      return fetcher.patch<AmendReceiptRoomBookingItemsResultDTO>(
        `${config.apiUrl}/billing/receipts/${id}/room-booking-items`,
        body,
      );
    },
    async getVoidPreview(id: string): Promise<VoidReceiptPreviewDTO> {
      return fetcher.get<VoidReceiptPreviewDTO>(
        `${config.apiUrl}/billing/receipts/${id}/void-preview`,
      );
    },
    async voidReceipt(
      id: string,
      body: { reason: string },
    ): Promise<VoidReceiptResultDTO> {
      return fetcher.post<VoidReceiptResultDTO>(
        `${config.apiUrl}/billing/receipts/${id}/void`,
        body,
      );
    },
  },

  creditInvoices: {
    async list(query: CreditInvoicesListQuery): Promise<{
      data: CreditInvoiceListItemDTO[];
      meta: { total: number; offset: number; limit: number };
    }> {
      const url = new URL(`${config.apiUrl}/billing/credit-invoices`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.refType) url.searchParams.set("refType", query.refType);
      if (query.outstandingOnly) url.searchParams.set("outstandingOnly", "1");
      const name = query.name?.trim();
      if (name) url.searchParams.set("name", name);
      if (query.sort?.length)
        url.searchParams.set("sort", JSON.stringify(query.sort));
      return fetcher.get(url.toString());
    },
    async getById(id: string): Promise<CreditInvoiceDetailDTO> {
      return fetcher.get<CreditInvoiceDetailDTO>(
        `${config.apiUrl}/billing/credit-invoices/${id}`,
      );
    },

    async getVoidPreview(id: string): Promise<VoidCreditInvoicePreviewDTO> {
      return fetcher.get<VoidCreditInvoicePreviewDTO>(
        `${config.apiUrl}/billing/credit-invoices/${id}/void-preview`,
      );
    },

    async voidInvoice(
      id: string,
      body: { reason: string },
    ): Promise<VoidCreditInvoiceResultDTO> {
      return fetcher.post<VoidCreditInvoiceResultDTO>(
        `${config.apiUrl}/billing/credit-invoices/${id}/void`,
        body,
      );
    },

    async bulkVoid(body: {
      invoiceIds: string[];
      reason: string;
    }): Promise<BulkVoidCreditInvoicesResultDTO> {
      return fetcher.post<BulkVoidCreditInvoicesResultDTO>(
        `${config.apiUrl}/billing/credit-invoices/bulk-void`,
        body,
      );
    },
    async applyPayment(
      invoiceId: string,
      body: { amount: number; notes?: string },
    ): Promise<CreditInvoiceDetailDTO> {
      return fetcher.post<CreditInvoiceDetailDTO>(
        `${config.apiUrl}/billing/credit-invoices/${invoiceId}/payments`,
        body,
      );
    },
    async reconcilePayment(paymentId: string): Promise<{ ok: boolean }> {
      return fetcher.post<{ ok: boolean }>(
        `${config.apiUrl}/billing/credit-payments/${paymentId}/reconcile`,
        {},
      );
    },
    async voidPayment(
      paymentId: string,
      body: { reason: string },
    ): Promise<CreditInvoiceDetailDTO> {
      return fetcher.post<CreditInvoiceDetailDTO>(
        `${config.apiUrl}/billing/credit-payments/${paymentId}/void`,
        body,
      );
    },
  },
};
